import { Box, HeaderTabLink, Line, Skeleton } from "@prismicio/editor-ui"
import { Suspense } from "react"

import { env } from "../../app/env"
import { releasesTabFlag } from "../../app/experiments"
import { Link } from "../../app/routing/Link"
import {
  headerHeight,
  mainContentMaxWidth,
  sidePanelWidth
} from "../../document/documentLayout"
import { useHasCustomTypes } from "../DocumentListBody/useHasCustomTypes"
import { ReleasesDropdownMenu } from "../releases/ReleasesDropdownMenu"
import { useSearchDocumentParams } from "../useDocumentList"
import { EnvironmentsDropdownMenu } from "./EnvironmentsDropdownMenu"
import { RepositoryAvatar } from "./RepositoryAvatar"
import { RepositoryTitle } from "./RepositoryTitle"
import { SearchBar, SearchBarSkeleton } from "./SearchBar"

type Tab = "working" | "archived" | "upcoming"

interface DocumentListHeaderProps {
  tab: Tab | undefined
}

export function DocumentListHeader(props: DocumentListHeaderProps) {
  const { tab } = props

  return (
    <Box
      display="flex"
      flexDirection="column"
      position="fixed"
      top={0}
      height={headerHeight}
      border={{ bottom: true }}
      backgroundColor="grey2"
      minWidth="100%"
      width="100vw"
    >
      <Line color="purple" />
      <Box justifyContent="center" flexGrow={1}>
        <Box
          width={mainContentMaxWidth}
          justifyContent="center"
          alignItems="center"
          gap={24}
        >
          <Box padding={{ bottom: 12, top: 12 }}>
            <RepositoryDetails />
          </Box>

          <HeaderNavigationBar tab={tab} />

          <Box padding={{ bottom: 12, top: 12 }} flexGrow={1}>
            <Suspense fallback={<SearchBarSkeleton />}>
              <SearchBar />
            </Suspense>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

function RepositoryDetails() {
  const dashboardUrl = `https://${env.PRISMIC_DOMAIN}/dashboard`

  return (
    <Box gap={16} alignItems="center" minWidth={sidePanelWidth}>
      <Link to={dashboardUrl}>
        <RepositoryAvatar />
      </Link>

      <Box flexDirection="column">
        <RepositoryTitle />
        <EnvironmentsDropdownMenu />
      </Box>
    </Box>
  )
}

interface HeaderNavigationBarProps {
  tab: Tab | undefined
}

function HeaderNavigationBar(props: HeaderNavigationBarProps) {
  const { tab } = props

  return (
    <Suspense fallback={<HeaderNavigationBarSkeleton />}>
      <HeaderNavigationBarContent tab={tab} />
    </Suspense>
  )
}

function HeaderNavigationBarContent(props: HeaderNavigationBarProps) {
  const { tab } = props

  const { urlSearchParams } = useSearchDocumentParams()
  const hasNoCustomTypes = !useHasCustomTypes()
  const releasesFlag = releasesTabFlag() === "on"
  const disableTabs = tab === undefined || hasNoCustomTypes

  return (
    <Box gap={16} height="100%" position="relative" left={-8}>
      <HeaderTabLink
        text="Work"
        renderLink={(children) => (
          <Link to={`/working${urlSearchParams}`} scrollToTop>
            {children}
          </Link>
        )}
        disabled={disableTabs}
        selected={tab === "working"}
      />
      <HeaderTabLink
        text="Planned"
        renderLink={(children) =>
          releasesFlag ? (
            <Link to={`/upcoming${urlSearchParams}`} scrollToTop>
              <ReleasesDropdownMenu>{children}</ReleasesDropdownMenu>
            </Link>
          ) : (
            children
          )
        }
        disabled={!releasesFlag || disableTabs}
        selected={tab === "upcoming"}
      />
      <HeaderTabLink
        text="Archived"
        renderLink={(children) => (
          <Link to={`/archived${urlSearchParams}`} scrollToTop>
            {children}
          </Link>
        )}
        disabled={disableTabs}
        selected={tab === "archived"}
      />
    </Box>
  )
}

function HeaderNavigationBarSkeleton() {
  return (
    <Box
      gap={16}
      height="100%"
      position="relative"
      left={-8}
      alignItems="center"
    >
      <Skeleton width={51} height={24} />
      <Skeleton width={95} height={24} />
      <Skeleton width={76} height={24} />
    </Box>
  )
}
